import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  deleteFamilyNews,
  getFamilyNews,
} from "../../../Services/familyNewsService";
import CreateFamilyNewsModal from "./CreateFamilyNewsModal";
import FamilyNewsList from "./FamilyNewsList";

function FamilyNews() {
  const [familyTreeId, setFamilyTreeId] = useState("");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState("Add");
  const [activeNews, setActiveNews] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [photoViewer, setPhotoViewer] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setFamilyTreeId(getFamilyTreeIdFromStorage() || "");
  }, []);

  const loadNews = useCallback(async () => {
    if (!familyTreeId) return;
    setLoading(true);
    try {
      setNews(await getFamilyNews(familyTreeId));
    } catch (error) {
      console.error("Error loading family news:", error);
      setNews([]);
      toast.error(error?.message || "Failed to load family news");
    } finally {
      setLoading(false);
    }
  }, [familyTreeId]);

  useEffect(() => {
    loadNews();
  }, [loadNews]);

  /** Archived entries live in their folder and are not listed here. */
  const currentNews = useMemo(
    () => news.filter((item) => !item.archived),
    [news]
  );

  /** Client-side search across the fields shown on the card. */
  const visibleNews = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return currentNews;
    const names = (people) =>
      (people || []).map((person) => person?.name || "").join(" ");
    return currentNews.filter((item) =>
      [
        item.title,
        item.theme,
        item.location,
        item.activities,
        item.date,
        names(item.hosts),
        names(item.attendees),
        (item.guests || []).join(" "),
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [currentNews, search]);

  // The pristine empty state carries its own "Add Family News" button, so the
  // top controls only appear once there is something to search through.
  const showTopControls = !loading && (search !== "" || currentNews.length > 0);

  const openAddEditModal = (mode, item) => {
    setEditMode(mode);
    setActiveNews(item || null);
    setShowModal(true);
  };

  const handleModalClose = (shouldRefresh) => {
    setShowModal(false);
    setActiveNews(null);
    if (shouldRefresh) loadNews();
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      // The interceptor resolves a failed request with the error response, so
      // only an explicit 200 counts as deleted.
      const response = await deleteFamilyNews(deleteTarget.id);
      if (response?.statusCode !== 200) {
        toast.error(response?.message || "Could not delete this news item");
        return;
      }
      toast.success(response?.message || "Family news deleted");
      setDeleteTarget(null);
      loadNews();
    } catch (error) {
      console.error("Error deleting family news:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not delete this news item"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="family-tree-container events family-news">
      {/* ===== HEADER ===== */}
      {showTopControls && (
        <div className="family-tree-top">
          <div className="right-controls">
            <div style={{ position: "relative", flex: "1 1 auto", minWidth: 0 }}>
              <input
                type="text"
                className="events-search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={search ? { paddingRight: "32px" } : undefined}
              />
              {search && (
                <span
                  onClick={() => setSearch("")}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#555",
                    fontSize: "16px",
                    lineHeight: 1,
                    fontWeight: "bold",
                    userSelect: "none",
                  }}
                >
                  ×
                </span>
              )}
            </div>
            <div className="icon-tooltip-wrapper">
              <button
                className="events-add-btn"
                onClick={() => openAddEditModal("Add", null)}
              >
                + Add
              </button>
              <div className="icon-tooltip">Add Family News</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== CARDS ===== */}
      <FamilyNewsList
        news={visibleNews}
        loading={loading}
        isSearching={search.trim() !== ""}
        openAddEditModal={openAddEditModal}
        openDeleteModal={setDeleteTarget}
        onOpenPhotos={setPhotoViewer}
      />

      {/* ===== ADD / EDIT / VIEW ===== */}
      <Modal
        isOpen={showModal}
        onRequestClose={() => handleModalClose(false)}
        contentLabel="Family News"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="event-moodal popup-card addmember-card create-event-modal family-news-modal"
        ariaHideApp={false}
      >
        <CreateFamilyNewsModal
          isOpen={showModal}
          editMode={editMode}
          newsDetails={activeNews}
          familyTreeId={familyTreeId}
          onClose={handleModalClose}
        />
      </Modal>

      {/* ===== PHOTO VIEWER ===== */}
      <Modal
        isOpen={!!photoViewer}
        onRequestClose={() => setPhotoViewer(null)}
        contentLabel="Family News Photos"
        overlayClassName="popup-overlay invite-overlay"
        portalClassName="event-moodal popup-card addmember-card create-event-modal family-news-photo-modal"
        ariaHideApp={false}
      >
        <button
          type="button"
          className="event-modal-close"
          onClick={() => setPhotoViewer(null)}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="event-modal-title">
          {photoViewer?.title || "Family News"} — Photos
        </h2>
        <div className="family-news-photo-viewer">
          {(photoViewer?.photos || []).map((photo) => (
            <img key={photo.id} src={photo.dataUrl} alt={photo.name || "Photo"} />
          ))}
        </div>
      </Modal>

      {/* ===== DELETE CONFIRMATION ===== */}
      <Modal
        isOpen={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            border: "none",
            background: "transparent",
            padding: 0,
            maxWidth: "420px",
            width: "100%",
          },
          overlay: { backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1000 },
        }}
        portalClassName="logOut"
        contentLabel="Delete Family News"
        ariaHideApp={false}
      >
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <span>
                <img src={window.getPublicUrl("/assets/images/logout.png")} alt="" />
              </span>
              <button
                type="button"
                className="close"
                onClick={() => setDeleteTarget(null)}
                aria-label="Close"
              >
                <img src={window.getPublicUrl("/assets/images/close.svg")} alt="" />
              </button>
            </div>
          </div>
          <div className="modal-body">
            <h5 className="modal-body-hading">Delete Family News</h5>
            <p className="modal-peragrapgh">
              Are you sure you want to delete this news item? This permanently
              removes it and its photos.
            </p>
          </div>
          <div
            className="modal-footer"
            style={{ display: "flex", justifyContent: "center", gap: "12px" }}
          >
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => setDeleteTarget(null)}
              style={{ width: "170px", height: "48px" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-block"
              onClick={confirmDelete}
              disabled={deleting}
              style={{ width: "170px", height: "48px" }}
            >
              {deleting ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FamilyNews;
